/**
 * Boot-time auth refresh. The access token lives only in memory (Zustand
 * state, not persisted) so a page reload always starts without one. If
 * the HttpOnly refresh cookie is still valid, /api/auth/refresh returns
 * a fresh access token without bouncing the user to the login screen.
 */
import { useAuthStore } from './store';

declare const __API_BASE__: string;

export async function tryBootstrapAuth(): Promise<void> {
  const state = useAuthStore.getState();
  if (state.access) return; // already authenticated this session
  if (!state.user)   return; // no previous session at all - go to /login
  try {
    const res = await fetch(`${__API_BASE__}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      useAuthStore.getState().logout();
      return;
    }
    const body = await res.json();
    if (body?.ok && body.data?.access) {
      const prev = state.user!;
      const nextUser = body.data.user
        ? {
            ...prev,
            ...body.data.user,
            memberid: body.data.user.memberid ?? prev.memberid,
          }
        : prev;
      useAuthStore.getState().setSession(
        body.data.access,
        nextUser,
        body.data.config ?? state.config,
      );
    } else {
      useAuthStore.getState().logout();
    }
  } catch {
    // No refresh cookie reachable — clear stale persisted user so API calls do not 401-loop.
    useAuthStore.getState().logout();
  }
}
